import { RESPONSE_RESULTS } from "../actions/constants";
import CustomIcon from "../components/layouts/CustomIcon";
import { DIRECTION_BACKWARD, DIRECTION_FORWARD, DIRECTION_SIBLING } from "../components/toolkit/actions/constants";

// compare and fetch result
export function compareAnswerPath(taskAnswers = [], answers = [], treeData = []) {
  const clientPath = taskAnswers.map((client) => getNodePath(treeData, client));

  let direct = true;
  let success = false;
  const userEnd = answers[answers.length - 1];
  for (let i = 0; i < clientPath.length; i++) {
    if (userEnd.id === clientPath[i][clientPath[i].length - 1].id) {
      success = true;
      break;
    }
  }

  const userDirectPath = getNodePath(treeData, userEnd?.id);
  if (userDirectPath.length !== answers.length) direct = false;
  else {
    for (var i = 0, l = userDirectPath.length; i < l; i++) {
      if (userDirectPath[i].id !== answers[i].id) {
        direct = false;
        break;
      }
    }
  }

  if (success && direct) return RESPONSE_RESULTS.DIRECT_SUCCESS;
  if (success && !direct) return RESPONSE_RESULTS.INDIRECT_SUCCESS;
  if (!success && direct) return RESPONSE_RESULTS.DIRECT_FAILURE;
  if (!success && !direct) return RESPONSE_RESULTS.INDIRECT_FAILURE;
  return RESPONSE_RESULTS.DIRECT_SKIPPED;
}

// Get Node Path via nodeId — searches across all home (root) nodes
export function getNodePath(treeData = [], nodeId, getPathString = false) {
  const roots = Array.isArray(treeData) ? treeData : [treeData];

  // Find a node by id across every root
  const findInRoots = (id) => {
    for (const root of roots) {
      const found = findNode(root, id);
      if (found) return found;
    }
    return null;
  };

  let item = findInRoots(nodeId);
  const path = [];

  while (item && item.parentId !== null) {
    path.unshift({ id: item.id, name: item.name });
    item = findInRoots(item.parentId);
  }

  if (item) {
    path.unshift({ id: item.id, name: item.name });
  }

  return getPathString ? path.map((node) => node.name).join(" > ") : path;
}

// find node
export function findNode(node, nodeId) {
  if (!node) return null;

  if (node?.id === nodeId) return node;

  if (node?.children?.length) {
    for (const child of node.children) {
      const found = findNode(child, nodeId);
      if (found) return found; // stop immediately once found
    }
  }

  return null;
}

const getDirectionIcon = (dir) => {
  switch (dir) {
    case DIRECTION_BACKWARD:
      return <CustomIcon name='backward' />;
    case DIRECTION_SIBLING:
      return <CustomIcon name='sibling' />;
    default:
      return <CustomIcon name='forward' />;
  }
};

const getDirectionText = (dir) => {
  switch (dir) {
    case DIRECTION_BACKWARD:
      return "<<<<";
    case DIRECTION_SIBLING:
      return "<<-->>";
    default:
      return ">>";
  }
};

// Get Path taken by user
export function getPathTakenByUser(answer = []) {
  return answer.reduce(
    (acc, { label = "", direction }) => {
      const dir = direction || DIRECTION_FORWARD;

      const icon = acc.answer ? getDirectionIcon(dir) : null;
      const textSymbol = acc.answerText ? getDirectionText(dir) : "";

      return {
        answer: (
          <span className='flex-item gap-5px'>
            {acc.answer}
            {icon}
            <span className='min-fit-content'>{label}</span>
          </span>
        ),
        answerArr: [...acc.answerArr, { label, icon }],
        answerText: `${acc.answerText} ${textSymbol} ${label}`.trim(),
      };
    },
    { answer: "", answerText: "", answerArr: [] }
  );
}

const toStats = (map, total) => {
  return Object.entries(map).map(([key, value]) => ({
    ...value,
    label: key,
    percentage: +((value?.count / total) * 100).toFixed(2),
  }));
};

// Get End Screen Result && Most Common Paths
export function getUserPathAnalytics(data = []) {
  const pathMap = {};
  const endScreenMap = {};
  const totalUsers = data?.length || 0;

  for (const { user, answer } of data) {
    if (!answer?.length) continue;

    // -------- End screen --------
    const last = answer[answer.length - 1].label;

    if (!endScreenMap[last]) {
      endScreenMap[last] = { count: 0, users: [] };
    }
    endScreenMap[last].count++;
    endScreenMap[last].users.push(user);

    // -------- Full path --------
    const pathTakenObj = getPathTakenByUser(answer);
    const key = pathTakenObj?.answerText;

    if (!pathMap[key]) {
      pathMap[key] = { count: 0, users: [], pathTakenObj: {} };
    }

    pathMap[key].count++;
    pathMap[key].users.push(user);
    pathMap[key].pathTakenObj = pathTakenObj;
  }

  return {
    totalUsers,
    endScreens: toStats(endScreenMap, totalUsers),
    commonPaths: toStats(pathMap, totalUsers).sort((a, b) => b.count - a.count),
  };
}
